import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { logoutUser, setEutcAccepted } from '../store/authSlice';
import { acceptEutc, getTermsDocuments } from '../services/termsService';
import { TermsDocument } from '../types/terms';

/** Tolerance for "scrolled to the bottom" — browsers report fractional scroll positions. */
const SCROLL_BOTTOM_EPSILON_PX = 16;

/**
 * Blocking End User Terms & Conditions (EUTC) acceptance gate. Shown after
 * login whenever the backend reports termsStatus.eutcAccepted === false; a
 * missing termsStatus (older backend) means no gate. The "I Agree" button
 * unlocks only once the user has scrolled the full document.
 */
const EutcAcceptanceGate: React.FC = () => {
  const dispatch = useAppDispatch();
  const termsStatus = useAppSelector((state) => state.auth?.termsStatus);
  const isGateOpen = termsStatus?.eutcAccepted === false;

  const [eutcDocument, setEutcDocument] = useState<TermsDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  // Bumped by Retry so the fetch effect re-runs after a failure
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isGateOpen || eutcDocument) {
      return;
    }

    let isMounted = true;
    const fetchTerms = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const termsData = await getTermsDocuments();
        if (isMounted) {
          setEutcDocument(termsData?.eutc ?? null);
          if (!termsData?.eutc) {
            setLoadError('The terms document is not available right now. Please try again.');
          }
        }
      } catch (fetchError: any) {
        console.error('[EutcAcceptanceGate] Failed to load EUTC document:', fetchError);
        if (isMounted) {
          setLoadError(fetchError?.message || 'Failed to load the terms document. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTerms();
    return () => {
      isMounted = false;
    };
  }, [isGateOpen, eutcDocument, fetchAttempt]);

  const isAtBottom = (element: HTMLElement): boolean =>
    element.scrollTop + element.clientHeight >= element.scrollHeight - SCROLL_BOTTOM_EPSILON_PX;

  // Enable "I Agree" immediately when the content does not overflow. The
  // scrollHeight > 0 guard skips environments without real layout (jsdom),
  // where every measurement is 0 and would falsely count as "no overflow".
  const checkContentOverflow = useCallback(() => {
    const scrollBox = scrollBoxRef.current;
    if (!scrollBox) return;
    if (scrollBox.scrollHeight > 0 && scrollBox.scrollHeight <= scrollBox.clientHeight + SCROLL_BOTTOM_EPSILON_PX) {
      setHasScrolledToBottom(true);
    }
  }, []);

  useEffect(() => {
    if (isGateOpen && eutcDocument) {
      checkContentOverflow();
    }
  }, [isGateOpen, eutcDocument, checkContentOverflow]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (isAtBottom(event.currentTarget)) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAgree = async () => {
    try {
      setAccepting(true);
      setSubmitError(null);
      await acceptEutc();
      // Update local state so the gate closes without a reload
      dispatch(setEutcAccepted());
    } catch (acceptError: any) {
      console.error('[EutcAcceptanceGate] Failed to record acceptance:', acceptError);
      setSubmitError(acceptError?.message || 'Failed to record your acceptance. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await dispatch(logoutUser());
    setLoggingOut(false);
  };

  const handleRetryLoad = () => {
    setLoadError(null);
    setEutcDocument(null);
    setFetchAttempt((attempt) => attempt + 1);
  };

  if (!isGateOpen) {
    return null;
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      disableEscapeKeyDown
      aria-labelledby="eutc-gate-title"
      // No onClose: the gate must not be dismissable via backdrop or Escape
    >
      <DialogTitle id="eutc-gate-title">
        {eutcDocument?.title || 'End User Terms & Conditions'}
        {eutcDocument?.lastUpdated && (
          <Typography variant="caption" color="textSecondary" className="block">
            Last updated: {eutcDocument.lastUpdated}
            {eutcDocument?.version ? ` (version ${eutcDocument.version})` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Please read the terms below. You must scroll to the end of the document before you can agree.
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && loadError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleRetryLoad}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}

        {!loading && !loadError && eutcDocument && (
          <Box
            ref={scrollBoxRef}
            onScroll={handleScroll}
            data-testid="eutc-scroll-container"
            sx={{ maxHeight: '55vh', overflow: 'auto', pr: 1 }}
          >
            {(eutcDocument?.sections ?? []).map((section, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                {section?.heading && (
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} gutterBottom>
                    {section.heading}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {section?.body}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout} color="error" disabled={accepting || loggingOut}>
          Log out
        </Button>
        <Button
          onClick={handleAgree}
          variant="contained"
          disabled={!hasScrolledToBottom || accepting || loggingOut || loading || !eutcDocument}
          startIcon={accepting ? <CircularProgress size={18} /> : null}
        >
          {accepting ? 'Submitting...' : 'I Agree'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EutcAcceptanceGate;
